import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { NotasFiscaisBlockedModal } from "@/components/NotasFiscaisBlockedModal";
import { getPlanInfo } from "@/integrations/supabase/plan-utils";
import { 
  FileText, 
  Download, 
  RefreshCw, 
  Search, 
  Filter,
  X,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Eye,
  Mail,
  Trash2,
  Edit,
  FileCheck,
  Calendar,
  TrendingUp,
  Package,
  FileEdit,
  MoreVertical,
  Loader2
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/components/AuthProvider";
import { DialogEmitirNFAvulsa } from "@/components/DialogEmitirNFAvulsa";
import { focusNFService } from "@/integrations/focusnf/service";
import { getCurrentEmpresaId } from "@/integrations/supabase/auth-utils";
import { getOrderByCode } from "@/integrations/supabase/orders";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatCurrency } from "@/utils/formatCurrency";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import JSZip from "jszip";

type FocusNFNota = {
  id?: string;
  empresa_id: string;
  order_id?: string;
  order_code?: string;
  ref: string;
  tipo_nota: 'NFe' | 'NFSe' | 'NFCe' | 'CTe' | 'MDFe' | 'NFCom' | 'MDe';
  status: string;
  numero?: string;
  serie?: string;
  chave_acesso?: string;
  valor_total?: number;
  xml_url?: string;
  danfe_url?: string;
  ambiente?: string;
  erro_mensagem?: string;
  dados_enviados?: Record<string, unknown>;
  dados_retornados?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
};

export default function GestaoNotasFiscais() {
  const navigate = useNavigate();
  const { empresa } = useAuth();
  const [notas, setNotas] = useState<FocusNFNota[]>([]);
  const [loading, setLoading] = useState(true);
  const [consultandoNota, setConsultandoNota] = useState<string | null>(null);
  const [focusConfig, setFocusConfig] = useState<any | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [filtroTipo, setFiltroTipo] = useState<string>('todos');
  const [busca, setBusca] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(10);
  const [dialogReemitirOpen, setDialogReemitirOpen] = useState(false);
  const [notaParaReemitir, setNotaParaReemitir] = useState<FocusNFNota | null>(null);
  const [reemitindo, setReemitindo] = useState(false);
  const [dialogEmitirAvulsaOpen, setDialogEmitirAvulsaOpen] = useState(false);
  const [selectedNotaRefs, setSelectedNotaRefs] = useState<string[]>([]);
  const [selectionScope, setSelectionScope] = useState<'pagina' | 'filtros'>('pagina');
  const [exportandoZip, setExportandoZip] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [hasNFAccess, setHasNFAccess] = useState<boolean | null>(null);
  const accessCheckDone = useRef(false);

  useEffect(() => {
    if (accessCheckDone.current) return;
    if (!empresa?.id) {
      console.log("❌ GestaoNotasFiscais: empresa?.id não disponível");
      return;
    }

    accessCheckDone.current = true;
    console.log("🔐 GestaoNotasFiscais: Verificando acesso a NF para empresa:", empresa.id);

    getPlanInfo(empresa.id).then(planInfo => {
      console.log("📊 GestaoNotasFiscais: Resultado getPlanInfo:", { canUseNF: planInfo.canUseNF, type: planInfo.type });
      if (!planInfo.canUseNF) {
        console.log("🔒 GestaoNotasFiscais: Bloqueando acesso - canUseNF é false");
        setHasNFAccess(false);
        setShowBlockedModal(true);
      } else {
        console.log("✅ GestaoNotasFiscais: Liberando acesso - canUseNF é true");
        setHasNFAccess(true);
        if (empresa?.tem_nota_fiscal) {
          loadNotas();
        } else {
          setLoading(false);
        }
      }
    }).catch((err) => {
      console.error("❌ GestaoNotasFiscais: Erro ao chamar getPlanInfo:", err);
      setHasNFAccess(true);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const loadConfig = async () => {
      const config = await focusNFService.getConfig();
      setFocusConfig(config);
    };

    loadConfig();
  }, []);

  const loadNotas = async () => {
    try {
      setLoading(true);
      const todasNotas = await focusNFService.listarNotas();
      setNotas(todasNotas);
      setSelectedNotaRefs([]);
    } catch (error: any) {
      console.error('Erro ao carregar notas:', error);
      toast.error('Erro ao carregar notas fiscais');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelecionarNota = (ref: string, checked: boolean) => {
    setSelectedNotaRefs((prev) => {
      if (checked) {
        return prev.includes(ref) ? prev : [...prev, ref];
      }
      return prev.filter((item) => item !== ref);
    });
  };

  const handleSelecionarTodas = (checked: boolean) => {
    if (checked) {
      const notasParaSelecionar = selectionScope === 'pagina' ? notasPaginadas : notasFiltradas;
      setSelectedNotaRefs(notasParaSelecionar.map((nota) => nota.ref));
      return;
    }
    setSelectedNotaRefs([]);
  };

  const exportarNotasSelecionadas = () => {
    const notasSelecionadas = notasFiltradas.filter((nota) => selectedNotaRefs.includes(nota.ref));
    if (notasSelecionadas.length === 0) {
      toast.error('Selecione ao menos uma nota para exportar');
      return;
    }

    const csvHeader = [
      'ref',
      'tipo_nota',
      'status',
      'numero',
      'serie',
      'chave_acesso',
      'valor_total',
      'order_code',
      'ambiente',
      'data_emissao',
      'xml_url',
      'danfe_url'
    ];

    const csvLines = notasSelecionadas.map((nota) => {
      const values = [
        nota.ref,
        nota.tipo_nota,
        nota.status,
        nota.numero || '',
        nota.serie || '',
        nota.chave_acesso || '',
        String(nota.valor_total ?? ''),
        nota.order_code || '',
        nota.ambiente || '',
        nota.created_at ? format(new Date(nota.created_at), 'yyyy-MM-dd HH:mm:ss') : '',
        nota.xml_url || '',
        nota.danfe_url || ''
      ];
      return values.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',');
    });

    const csvContent = [csvHeader.join(','), ...csvLines].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `notas_fiscais_${format(new Date(), 'yyyyMMdd_HHmm')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Exportação iniciada');
  };

  const obterBaseUrlNota = (nota: FocusNFNota) =>
    nota.ambiente === 'producao' ? 'https://api.focusnfe.com.br' : 'https://homologacao.focusnfe.com.br';

  // Função auxiliar para construir URL de arquivo (XML ou PDF)
  // Detecta se é URL completa ou caminho relativo
  const construirUrlArquivo = (nota: FocusNFNota, urlCampo: string | undefined): string | null => {
    if (!urlCampo) return null;
    
    // Se já começa com http/https, é URL completa (S3, etc)
    if (urlCampo.startsWith('http://') || urlCampo.startsWith('https://')) {
      return urlCampo;
    }
    
    // Se começa com /, é caminho relativo - precisa de baseUrl
    if (urlCampo.startsWith('/')) {
      const baseUrl = obterBaseUrlNota(nota);
      return `${baseUrl}${urlCampo}`;
    }
    
    // Caso não esperado - retornar como está
    return urlCampo;
  };

  const exportarNotasZip = async (tipo: 'xml' | 'danfe' | 'ambos') => {
    let notasSelecionadas = notasFiltradas.filter((nota) => selectedNotaRefs.includes(nota.ref));
    if (notasSelecionadas.length === 0) {
      toast.error('Selecione ao menos uma nota para exportar');
      return;
    }

    setExportandoZip(true);
    try {
      // Se solicitando DANFE, consultar notas que não têm danfe_url preenchido
      if ((tipo === 'danfe' || tipo === 'ambos')) {
        const notasSemDanfe = notasSelecionadas.filter(
          (nota) => nota.status === 'autorizado' && !nota.danfe_url && (nota.tipo_nota === 'NFe' || nota.tipo_nota === 'NFSe' || nota.tipo_nota === 'NFCe')
        );
        
        if (notasSemDanfe.length > 0) {
          toast.loading(`Carregando PDFs de ${notasSemDanfe.length} nota(s)...`);
          
          // Consultar cada nota para preencher danfe_url
          await Promise.all(
            notasSemDanfe.map((nota) => 
              handleConsultarNota(nota.ref).catch((err) => {
                console.error(`Erro ao consultar nota ${nota.ref}:`, err);
              })
            )
          );
          
          // Recarregar as notas após as consultas
          await loadNotas();
          notasSelecionadas = notasFiltradas.filter((nota) => selectedNotaRefs.includes(nota.ref));
        }
      }

      const zip = new JSZip();
      let arquivosAdicionados = 0;

      await Promise.all(
        notasSelecionadas.map(async (nota) => {
          const xmlUrl = construirUrlArquivo(nota, nota.xml_url);
          const danfeUrl = construirUrlArquivo(nota, nota.danfe_url);
          const tasks: Array<Promise<void>> = [];

          if ((tipo === 'xml' || tipo === 'ambos') && xmlUrl) {
            tasks.push(
              fetch(xmlUrl)
                .then((res) => (res.ok ? res.blob() : null))
                .then((blob) => {
                  if (!blob) return;
                  const nomeArquivo = `${nota.tipo_nota}_${nota.ref}_xml.xml`;
                  zip.file(nomeArquivo, blob);
                  arquivosAdicionados += 1;
                })
            );
          }

          if ((tipo === 'danfe' || tipo === 'ambos') && danfeUrl) {
            tasks.push(
              fetch(danfeUrl)
                .then((res) => (res.ok ? res.blob() : null))
                .then((blob) => {
                  if (!blob) return;
                  const nomeArquivo = `${nota.tipo_nota}_${nota.ref}_danfe.pdf`;
                  zip.file(nomeArquivo, blob);
                  arquivosAdicionados += 1;
                })
            );
          }

          await Promise.all(tasks);
        })
      );

      if (arquivosAdicionados === 0) {
        toast.error('Nenhum arquivo disponível para exportação nas notas selecionadas');
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      const sufixo = tipo === 'ambos' ? 'xml_danfe' : tipo;
      link.download = `notas_fiscais_${sufixo}_${format(new Date(), 'yyyyMMdd_HHmm')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Exportação em lote concluída');
    } catch (error) {
      console.error('Erro ao exportar notas em lote:', error);
      toast.error('Não foi possível baixar os arquivos. Se estiver em localhost, o navegador pode bloquear o download (CORS).');
    } finally {
      setExportandoZip(false);
    }
  };

  const handleConsultarNota = async (ref: string) => {
    try {
      if (isHomologacaoSemToken) {
        toast.error('Token de homologação não configurado. Cadastre o emitente e preencha o token de homologação.');
        return;
      }

      setConsultandoNota(ref);
      const empresa_id = await getCurrentEmpresaId();

      // Buscar o tipo_nota da nota armazenada
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: notaExistente } = await supabase
        .from('focusnf_notas')
        .select('tipo_nota')
        .eq('ref', ref)
        .eq('empresa_id', empresa_id || '')
        .single();

      const tipoNota = notaExistente?.tipo_nota || 'NFe';
      console.log('[FocusNF] Consultando nota com tipoNota:', tipoNota);

      const result = await focusNFService.consultarNota(ref, false, tipoNota);
      
      if (result.ok && result.data) {
        // Atualizar nota no banco para limpar erros incorretos
        if (empresa_id) {
          const notaData = result.data;
          
          const { data: notaExistenteCompleta } = await supabase
            .from('focusnf_notas')
            .select('id')
            .eq('ref', ref)
            .eq('empresa_id', empresa_id)
            .single();

          if (notaExistenteCompleta) {
            // A resposta da API Focus NF tem campos específicos
            const responseData = notaData as any;
            
            // Mapear campos conforme o tipo de nota
            let xmlUrl = responseData.caminho_xml_nota_fiscal;
            let danfeUrl = responseData.caminho_danfe;
            
            // Para NFS-e, a API pode retornar campos diferentes
            if (tipoNota === 'NFSe') {
              xmlUrl = responseData.url || responseData.caminho_xml_nota_fiscal;
              danfeUrl = responseData.url_danfse || responseData.caminho_danfe_rps || responseData.caminho_danfe;
            }
            
            console.log('[GestaoNotasFiscais] Consultando nota:', {
              ref,
              tipoNota,
              xmlUrl,
              danfeUrl,
              responseData
            });
            
            await supabase
              .from('focusnf_notas')
              .update({
                tipo_nota: tipoNota,
                status: responseData.status || 'processando_autorizacao',
                numero: responseData.numero || responseData.numero_rps,
                serie: responseData.serie || responseData.serie_rps,
                chave_acesso: responseData.chave_nfe || responseData.chave_nfse || responseData.codigo_verificacao || responseData.chave_acesso,
                xml_url: xmlUrl,
                danfe_url: danfeUrl,
                dados_retornados: responseData,
                // Só salvar como erro se realmente for um erro (não autorizado)
                erro_mensagem: responseData.status === 'autorizado' || responseData.status === 'processando_autorizacao'
                  ? null // Limpar erro se autorizado
                  : (responseData.erro_mensagem || (responseData.status === 'erro_autorizacao' || responseData.status === 'denegado' ? responseData.mensagem_sefaz : null) || null),
                updated_at: new Date().toISOString(),
              })
              .eq('id', notaExistenteCompleta.id);
          }
        }
        
        toast.success('Status atualizado!');
        await loadNotas();
        setConsultandoNota(null);
      } else {
        toast.error(result.error || 'Erro ao consultar nota');
        setConsultandoNota(null);
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao consultar nota');
      setConsultandoNota(null);
    }
  };

  const [dialogCancelarOpen, setDialogCancelarOpen] = useState(false);
  const [notaParaCancelar, setNotaParaCancelar] = useState<string | null>(null);
  const [justificativaCancelamento, setJustificativaCancelamento] = useState('');
  const [cancelando, setCancelando] = useState(false);
  
  const [dialogCartaCorrecaoOpen, setDialogCartaCorrecaoOpen] = useState(false);
  const [notaParaCorrecao, setNotaParaCorrecao] = useState<string | null>(null);
  const [textoCorrecao, setTextoCorrecao] = useState('');
  const [emitindoCartaCorrecao, setEmitindoCartaCorrecao] = useState(false);
  const [excluindoNota, setExcluindoNota] = useState<string | null>(null);

  const handleCancelarNota = async (ref: string) => {
    setNotaParaCancelar(ref);
    setDialogCancelarOpen(true);
  };

  const confirmarCancelamento = async () => {
    if (!justificativaCancelamento || justificativaCancelamento.length < 15) {
      toast.error('Justificativa deve ter no mínimo 15 caracteres');
      return;
    }

    if (!notaParaCancelar) return;

    try {
      setCancelando(true);
      const result = await focusNFService.cancelarNota(notaParaCancelar, justificativaCancelamento);
      
      if (result.ok) {
        toast.success('Nota cancelada com sucesso!');
        setDialogCancelarOpen(false);
        setJustificativaCancelamento('');
        setNotaParaCancelar(null);
        await loadNotas();
      } else {
        toast.error(result.error || 'Erro ao cancelar nota');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao cancelar nota');
    } finally {
      setCancelando(false);
    }
  };

  const handleEnviarEmail = async (nota: FocusNFNota) => {
    const emailsInput = window.prompt('Informe os e-mails separados por vírgula', '');
    if (!emailsInput) return;

    const emails = emailsInput
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    if (emails.length === 0) {
      toast.error('Informe ao menos um e-mail válido');
      return;
    }

    if (emails.length > 10) {
      toast.error('É possível enviar no máximo 10 e-mails por vez');
      return;
    }

    try {
      const result = await focusNFService.enviarEmail(nota.ref, emails);
      if (result.ok) {
        toast.success('E-mails enviados com sucesso!');
      } else {
        toast.error(result.error || 'Erro ao enviar e-mails');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao enviar e-mails');
    }
  };

  const isHomologacaoSemToken =
    focusConfig?.ambiente === 'homologacao' &&
    !focusConfig?.token_homologacao?.trim();

  const handleAbrirReemitir = (nota: FocusNFNota) => {
    setNotaParaReemitir(nota);
    setDialogReemitirOpen(true);
  };

  const buildPayloadFromOrder = async (orderCode: string) => {
    const order = await getOrderByCode(orderCode);
    if (!order) {
      throw new Error('Pedido não encontrado para reconstruir a reemissão.');
    }

    let clienteData: any = {
      nome: order.customer_name,
      email: order.customer_email || undefined,
      telefone: order.customer_phone || undefined,
    };

    try {
      const empresa_id = await getCurrentEmpresaId();
      if (empresa_id && order.customer_name) {
        const { data: clientes } = await supabase
          .from('atelie_customers')
          .select('*')
          .eq('empresa_id', empresa_id)
          .ilike('name', order.customer_name)
          .limit(1);

        const cliente = clientes && clientes.length > 0 ? clientes[0] : null;
        if (cliente) {
          clienteData = {
            nome: cliente.name || clienteData.nome,
            email: cliente.email || clienteData.email,
            telefone: cliente.phone || clienteData.telefone,
            cpf_cnpj: cliente.cpf_cnpj || undefined,
            endereco: {
              logradouro: cliente.endereco_logradouro || undefined,
              numero: cliente.endereco_numero || undefined,
              complemento: cliente.endereco_complemento || undefined,
              bairro: cliente.endereco_bairro || undefined,
              cidade: cliente.endereco_cidade || undefined,
              uf: cliente.endereco_uf || undefined,
              cep: cliente.endereco_cep || undefined,
            },
          };
        }
      }
    } catch (error) {
      console.error('[GestaoNotasFiscais] Erro ao buscar cliente para reemissão:', error);
    }

    const valorTotal = Number(order.value || 0);
    const items = [
      {
        numero_item: 1,
        codigo_produto: order.code || order.id || '1',
        descricao: `Pedido ${order.code}`,
        ncm: '6204.62.00',
        cfop: '5102',
        unidade: 'UN',
        quantidade: 1,
        valor_unitario: valorTotal,
        valor_total: valorTotal,
      },
    ];

    return {
      orderCode: order.code || orderCode,
      tipoNota: 'NFe' as const,
      cliente: clienteData,
      valorTotal,
      items,
    };
  };

  const handleConfirmarReemitir = async () => {
    if (!notaParaReemitir) return;

    if (isHomologacaoSemToken) {
      toast.error('Token de homologação não configurado. Cadastre o emitente e preencha o token de homologação.');
      return;
    }

    if (!notaParaReemitir.order_code) {
      toast.error('Pedido relacionado não encontrado para reemissão.');
      return;
    }

    try {
      setReemitindo(true);
      const dadosEnviados = notaParaReemitir.dados_enviados as any;
      const payload = dadosEnviados?.cliente && dadosEnviados?.items
        ? {
            orderCode: notaParaReemitir.order_code,
            tipoNota: dadosEnviados.tipoNota || notaParaReemitir.tipo_nota,
            cliente: dadosEnviados.cliente,
            valorTotal: dadosEnviados.valorTotal,
            items: dadosEnviados.items,
          }
        : await buildPayloadFromOrder(notaParaReemitir.order_code);

      const result = await focusNFService.reemitirNota(
        notaParaReemitir.order_code,
        payload,
        'Reemissão solicitada pelo usuário'
      );

      if (result.ok) {
        toast.success('Nota reemitida com sucesso!');
        setDialogReemitirOpen(false);
        setNotaParaReemitir(null);
        await loadNotas();
      } else {
        toast.error(result.error || 'Erro ao reemitir nota');
      }
    } catch (error: any) {
      console.error('[GestaoNotasFiscais] Erro ao reemitir nota:', error);
      toast.error(error.message || 'Erro ao reemitir nota');
    } finally {
      setReemitindo(false);
    }
  };

  const handleEmitirCartaCorrecao = (ref: string) => {
    setNotaParaCorrecao(ref);
    setDialogCartaCorrecaoOpen(true);
  };

  const confirmarCartaCorrecao = async () => {
    if (!textoCorrecao.trim()) {
      toast.error('Texto da correção é obrigatório');
      return;
    }

    if (!notaParaCorrecao) return;

    try {
      setEmitindoCartaCorrecao(true);
      const result = await focusNFService.emitirCartaCorrecao(notaParaCorrecao, textoCorrecao);
      
      if (result.ok) {
        toast.success('Carta de Correção emitida com sucesso!');
        setDialogCartaCorrecaoOpen(false);
        setTextoCorrecao('');
        setNotaParaCorrecao(null);
        await loadNotas();
      } else {
        toast.error(result.error || 'Erro ao emitir carta de correção');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao emitir carta de correção');
    } finally {
      setEmitindoCartaCorrecao(false);
    }
  };

  const handleExcluirNota = async (notaId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta nota fiscal do sistema? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      setExcluindoNota(notaId);
      const { supabase } = await import('@/integrations/supabase/client');
      const { error } = await supabase
        .from('focusnf_notas')
        .delete()
        .eq('id', notaId);

      if (error) {
        throw error;
      }

      toast.success('Nota fiscal excluída com sucesso!');
      await loadNotas();
    } catch (error: any) {
      console.error('Erro ao excluir nota:', error);
      toast.error(error.message || 'Erro ao excluir nota fiscal');
    } finally {
      setExcluindoNota(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'autorizado':
        return <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" />Autorizado</Badge>;
      case 'processando_autorizacao':
        return <Badge className="bg-yellow-500"><Clock className="h-3 w-3 mr-1" />Processando</Badge>;
      case 'erro_emissao':
      case 'erro_autorizacao':
        return <Badge className="bg-red-500"><XCircle className="h-3 w-3 mr-1" />Erro</Badge>;
      case 'cancelado':
        return <Badge className="bg-gray-500"><X className="h-3 w-3 mr-1" />Cancelado</Badge>;
      case 'denegado':
        return <Badge className="bg-orange-500"><AlertCircle className="h-3 w-3 mr-1" />Denegado</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const agora = new Date();
  const anoAtual = agora.getFullYear();
  const mesAtual = agora.getMonth();

  const notasMesAtual = notas.filter((nota) => {
    if (!nota.created_at) return false;
    const data = new Date(nota.created_at);
    return data.getFullYear() === anoAtual && data.getMonth() === mesAtual;
  });

  // Define limite de notas por mês conforme o plano da empresa.
  // Se o campo plan_type ainda não estiver com os novos valores, usamos 100 como padrão.
  const resolverLimiteMensalNotas = () => {
    if (!empresa?.tem_nota_fiscal) return 0;

  const planType = (((empresa as any)?.plan_type as string) || "").toLowerCase();

    if (planType === "monthly-professional-plus") {
      return 300;
    }

    if (planType === "monthly-professional-enterprise") {
      return 1000;
    }

    // Qualquer outro plano com NF (inclusive profissional mensal/anual antigo): 100 notas/mês por padrão
    return 100;
  };

  const limiteMensalNotas = resolverLimiteMensalNotas();
  const usoMensalPercent =
    limiteMensalNotas > 0
      ? Math.round((notasMesAtual.length / limiteMensalNotas) * 100)
      : 0;

  const notasFiltradas = notas.filter(nota => {
    if (filtroStatus !== 'todos' && nota.status !== filtroStatus) return false;
    if (filtroTipo !== 'todos' && nota.tipo_nota !== filtroTipo) return false;
    if (busca) {
      const buscaLower = busca.toLowerCase();
      const clienteNome = (nota.dados_enviados as any)?.clienteNome || '';
      const matchRef = nota.ref.toLowerCase().includes(buscaLower);
      const matchNumero = nota.numero?.toLowerCase().includes(buscaLower);
      const matchOrder = nota.order_code?.toLowerCase().includes(buscaLower);
      const matchCliente = clienteNome.toLowerCase().includes(buscaLower);
      if (!matchRef && !matchNumero && !matchOrder && !matchCliente) return false;
    }
    if (dataInicio && nota.created_at && nota.created_at < dataInicio) return false;
    if (dataFim && nota.created_at && nota.created_at > dataFim + 'T23:59:59') return false;
    return true;
  });

  // Limpando seleções quando filtros mudam
  useEffect(() => {
    setSelectedNotaRefs([]);
  }, [filtroStatus, filtroTipo, busca, dataInicio, dataFim]);

  // Resetar para página 1 quando filtros mudam (dentro de useEffect!)
  useEffect(() => {
    if (paginaAtual > 1 && notasFiltradas.length === 0) {
      setPaginaAtual(1);
    }
  }, [notasFiltradas.length, paginaAtual]);

  // Paginação
  const totalPaginas = Math.ceil(notasFiltradas.length / itensPorPagina);
  const indiceInicio = (paginaAtual - 1) * itensPorPagina;
  const indiceFim = indiceInicio + itensPorPagina;
  const notasPaginadas = notasFiltradas.slice(indiceInicio, indiceFim);

  const estatisticas = {
    total: notas.length,
    autorizadas: notas.filter(n => n.status === 'autorizado').length,
    processando: notas.filter(n => n.status === 'processando_autorizacao').length,
    erros: notas.filter(n => n.status === 'erro_emissao' || n.status === 'erro_autorizacao').length,
    valorTotal: notas.filter(n => n.status === 'autorizado').reduce((acc, n) => acc + (n.valor_total || 0), 0)
  };

  // Primeiro: verificar acesso ao plano (tem prioridade)
  if (hasNFAccess === false) {
    return (
      <NotasFiscaisBlockedModal
        open={showBlockedModal}
        onOpenChange={setShowBlockedModal}
      />
    );
  }

  // Segundo: verificar se NF está habilitada na empresa (só checa se tem acesso)
  if (!empresa?.tem_nota_fiscal) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
        <div className="container mx-auto p-6">
          <Card>
            <CardHeader>
              <CardTitle>Gestão de Notas Fiscais</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Você precisa ter o plano Profissional (com NF) para acessar esta funcionalidade.
              </p>
              <Button onClick={() => navigate('/assinatura')}>
                Ver Planos
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Terceiro: renderizar conteúdo principal (têm acesso E NF está habilitada)
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      <PageHeader
        title="Gestão de Notas Fiscais"
        icon={<FileCheck className="w-5 h-5 text-purple-600" />}
        rightActions={
          <Button
            className="bg-green-600 hover:bg-green-700"
            onClick={() => setDialogEmitirAvulsaOpen(true)}
            size="sm"
          >
            <FileText className="h-4 w-4 mr-2" />
            Emitir NF Avulsa
          </Button>
        }
      />

      <div className="container mx-auto p-6 space-y-6">
        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card 
            className="cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all"
            onClick={() => {
              setFiltroStatus('todos');
              setPaginaAtual(1);
            }}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total de Notas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{estatisticas.total}</div>
              <p className="text-xs text-muted-foreground mt-1">Notas emitidas</p>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg hover:border-green-300 transition-all"
            onClick={() => {
              setFiltroStatus('autorizado');
              setPaginaAtual(1);
            }}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Autorizadas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{estatisticas.autorizadas}</div>
              <p className="text-xs text-muted-foreground mt-1">Notas autorizadas</p>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg hover:border-yellow-300 transition-all"
            onClick={() => {
              setFiltroStatus('processando_autorizacao');
              setPaginaAtual(1);
            }}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Processando</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{estatisticas.processando}</div>
              <p className="text-xs text-muted-foreground mt-1">Aguardando autorização</p>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg hover:border-purple-300 transition-all"
            onClick={() => {
              setPaginaAtual(1);
            }}
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Valor Total</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">{formatCurrency({ value: estatisticas.valorTotal, currency: 'BRL' })}</div>
              <p className="text-xs text-muted-foreground mt-1">Valor das notas autorizadas</p>
            </CardContent>
          </Card>
        </div>

        {isHomologacaoSemToken && (
          <Card className="border-red-200 bg-red-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-red-900">
                Token de homologação não configurado
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-red-800">
              Cadastre o emitente e preencha o token de homologação para consultar ou reemitir notas em homologação.
            </CardContent>
          </Card>
        )}

        {/* Resumo rápido de uso do limite mensal de notas */}
        {empresa?.tem_nota_fiscal && (
          <Card className="mt-2 border-blue-200 bg-blue-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-blue-900 flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-700" />
                Uso de notas fiscais neste mês
              </CardTitle>
              <CardDescription className="text-xs text-blue-700">
                Acompanhe quantas notas você já emitiu neste mês em relação ao limite aproximado do seu plano atual.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between">
                  <p className="text-sm text-blue-900">
                    <strong>{notasMesAtual.length}</strong> notas emitidas no mês atual
                  </p>
                  <p className="text-xs text-blue-800">
                    Limite estimado do plano: {limiteMensalNotas || 0} notas/mês
                  </p>
                </div>
                <div className="w-full h-2 rounded-full bg-blue-100 overflow-hidden">
                  <div
                    className={`h-2 rounded-full ${
                      usoMensalPercent >= 95
                        ? "bg-red-500"
                        : usoMensalPercent >= 80
                        ? "bg-amber-500"
                        : "bg-blue-500"
                    }`}
                    style={{ width: `${Math.min(usoMensalPercent, 100)}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-blue-800">
                    Uso estimado: {usoMensalPercent}%
                  </span>
                  {usoMensalPercent >= 95 ? (
                    <span className="text-red-700 font-medium">
                      Quase no limite. Considere evoluir para um plano com mais notas.
                    </span>
                  ) : usoMensalPercent >= 80 ? (
                    <span className="text-amber-700 font-medium">
                      Você já usou mais de 80% do limite mensal de notas.
                    </span>
                  ) : (
                    <span className="text-blue-700">
                      Você ainda está dentro de um uso confortável neste mês.
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filtros */}
        <Card>
          <CardHeader>
            <CardTitle>Filtros</CardTitle>
            <CardDescription>Filtre suas notas fiscais por status, tipo, data ou busca</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por ref, número ou pedido..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="pl-9"
                />
              </div>

              <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os Status</SelectItem>
                  <SelectItem value="autorizado">Autorizado</SelectItem>
                  <SelectItem value="processando_autorizacao">Processando</SelectItem>
                  <SelectItem value="erro_emissao">Erro</SelectItem>
                  <SelectItem value="cancelado">Cancelado</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os Tipos</SelectItem>
                  <SelectItem value="NFe">NFe</SelectItem>
                  <SelectItem value="NFCe">NFCe</SelectItem>
                  <SelectItem value="NFSe">NFSe</SelectItem>
                </SelectContent>
              </Select>

              <Input
                type="date"
                placeholder="Data Início"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
              />

              <Input
                type="date"
                placeholder="Data Fim"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
              />
            </div>

            {(filtroStatus !== 'todos' || filtroTipo !== 'todos' || busca || dataInicio || dataFim) && (
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => {
                  setFiltroStatus('todos');
                  setFiltroTipo('todos');
                  setBusca('');
                  setDataInicio('');
                  setDataFim('');
                }}
              >
                <X className="h-4 w-4 mr-2" />
                Limpar Filtros
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Lista de Notas */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Notas Fiscais</CardTitle>
                <CardDescription>
                  {notasFiltradas.length} {notasFiltradas.length === 1 ? 'nota encontrada' : 'notas encontradas'}
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {selectedNotaRefs.length > 0 && (
                  <>
                    <Button variant="outline" size="sm" onClick={exportarNotasSelecionadas}>
                      <Download className="h-4 w-4 mr-2" />
                      Exportar CSV ({selectedNotaRefs.length})
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportarNotasZip('xml')} disabled={exportandoZip}>
                      {exportandoZip ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FileCheck className="h-4 w-4 mr-2" />
                      )}
                      Exportar XML (ZIP)
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportarNotasZip('danfe')} disabled={exportandoZip}>
                      {exportandoZip ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FileText className="h-4 w-4 mr-2" />
                      )}
                      Exportar DANFE (ZIP)
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => exportarNotasZip('ambos')} disabled={exportandoZip}>
                      {exportandoZip ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Exportar XML + DANFE (ZIP)
                    </Button>
                  </>
                )}
                <Button variant="outline" size="sm" onClick={loadNotas} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>
            </div>
            {notasFiltradas.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border bg-white p-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={
                      selectedNotaRefs.length > 0 &&
                      selectedNotaRefs.length ===
                        (selectionScope === 'pagina' ? notasPaginadas.length : notasFiltradas.length)
                    }
                    onCheckedChange={(checked) => handleSelecionarTodas(Boolean(checked))}
                  />
                  <span className="text-sm">
                    Selecionar todas ({selectedNotaRefs.length}/{notasFiltradas.length})
                  </span>
                </div>
                <Select value={selectionScope} onValueChange={(value) => setSelectionScope(value as 'pagina' | 'filtros')}>
                  <SelectTrigger className="w-56">
                    <SelectValue placeholder="Escopo de seleção" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pagina">Selecionar só esta página</SelectItem>
                    <SelectItem value="filtros">Selecionar todas dos filtros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                <p className="text-muted-foreground mt-2">Carregando notas fiscais...</p>
              </div>
            ) : notasFiltradas.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nenhuma nota fiscal encontrada</p>
              </div>
            ) : (
              <div className="space-y-4">
                {notasPaginadas.map((nota) => (
                  <Card key={nota.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Checkbox
                              checked={selectedNotaRefs.includes(nota.ref)}
                              onCheckedChange={(checked) => toggleSelecionarNota(nota.ref, Boolean(checked))}
                            />
                            <FileText className="h-5 w-5 text-purple-600" />
                            <div>
                              <h3 className="font-semibold">
                                {nota.tipo_nota} {nota.numero || nota.ref}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                Ref: {nota.ref}
                                {nota.order_code && ` • Pedido: ${nota.order_code}`}
                              </p>
                            </div>
                            {getStatusBadge(nota.status)}
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Cliente</p>
                              <p className="font-semibold text-sm truncate">{(nota.dados_enviados as any)?.clienteNome || '-'}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Valor</p>
                              <p className="font-semibold">{formatCurrency({ value: nota.valor_total || 0, currency: 'BRL' })}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Série/Numero</p>
                              <p className="font-semibold">{nota.serie || '-'}/{nota.numero || '-'}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Data Emissão</p>
                              <p className="font-semibold">
                                {nota.created_at ? format(new Date(nota.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR }) : '-'}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Ambiente</p>
                              <p className="font-semibold">{nota.ambiente === 'producao' ? 'Produção' : 'Homologação'}</p>
                            </div>
                          </div>

                          {nota.erro_mensagem && nota.status !== 'autorizado' && (
                            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                              <p className="text-sm text-red-800 font-medium">Erro:</p>
                              <p className="text-sm text-red-600">{nota.erro_mensagem}</p>
                              {(nota?.dados_retornados as any)?.requires_numero_update && (
                                <Button
                                  variant="link"
                                  className="mt-2 px-0 text-red-700"
                                  onClick={() =>
                                    navigate('/configuracao-focusnf', {
                                      state: { highlight: 'numeracao' },
                                    })
                                  }
                                >
                                  Ajustar próxima numeração
                                </Button>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="flex items-start gap-2 ml-4">
                          {/* Menu de ações com dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuItem
                                onClick={() => handleConsultarNota(nota.ref)}
                                disabled={consultandoNota === nota.ref || isHomologacaoSemToken}
                              >
                                <RefreshCw className={`h-4 w-4 mr-2 ${consultandoNota === nota.ref ? 'animate-spin' : ''}`} />
                                Atualizar status
                              </DropdownMenuItem>

                              {nota.status === 'autorizado' && (
                                <>
                                  {nota.xml_url && (
                                    <DropdownMenuItem
                                      onClick={() => {
                                        const url = construirUrlArquivo(nota, nota.xml_url);
                                        if (url) window.open(url, '_blank');
                                      }}
                                    >
                                      <Download className="h-4 w-4 mr-2" />
                                      Baixar XML
                                    </DropdownMenuItem>
                                  )}
                                  {nota.danfe_url && (
                                    <DropdownMenuItem
                                      onClick={() => {
                                        const url = construirUrlArquivo(nota, nota.danfe_url);
                                        if (url) window.open(url, '_blank');
                                      }}
                                    >
                                      <FileCheck className="h-4 w-4 mr-2" />
                                      Baixar {nota.tipo_nota === 'NFSe' ? 'PDF' : 'DANFE'}
                                    </DropdownMenuItem>
                                  )}
                                  {nota.tipo_nota === 'NFSe' && !nota.danfe_url && (
                                    <DropdownMenuItem
                                      onClick={() => handleConsultarNota(nota.ref)}
                                      disabled={consultandoNota === nota.ref}
                                    >
                                      {consultandoNota === nota.ref ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      ) : (
                                        <Download className="h-4 w-4 mr-2" />
                                      )}
                                      Carregar PDF da NFS-e
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={() => handleEmitirCartaCorrecao(nota.ref)}>
                                    <FileEdit className="h-4 w-4 mr-2" />
                                    Carta de Correção
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleEnviarEmail(nota)}>
                                    <Mail className="h-4 w-4 mr-2" />
                                    Enviar por Email
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => handleCancelarNota(nota.ref)}
                                    className="text-red-600 focus:text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Cancelar Nota
                                  </DropdownMenuItem>
                                </>
                              )}

                              {(nota.status === 'erro_emissao' || nota.status === 'erro_autorizacao' || nota.status === 'erro_validacao' || nota.status === 'cancelado') && (
                                <>
                                  <DropdownMenuItem
                                    onClick={() => handleAbrirReemitir(nota)}
                                    disabled={isHomologacaoSemToken}
                                  >
                                    <FileEdit className="h-4 w-4 mr-2" />
                                    Reemitir Nota
                                  </DropdownMenuItem>
                                  {nota.id && (
                                    <>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem 
                                        onClick={() => handleExcluirNota(nota.id!)}
                                        disabled={excluindoNota === nota.id}
                                        className="text-red-600 focus:text-red-600"
                                      >
                                        {excluindoNota === nota.id ? (
                                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                          <Trash2 className="h-4 w-4 mr-2" />
                                        )}
                                        Excluir do Sistema
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </>
                              )}

                              {nota.order_code && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => navigate(`/pedidos/${nota.order_code}`)}>
                                    <Package className="h-4 w-4 mr-2" />
                                    Ver Pedido Relacionado
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Controles de Paginação */}
            {notasFiltradas.length > itensPorPagina && (
              <div className="mt-6 flex items-center justify-between border-t pt-4">
                <div className="text-sm text-muted-foreground">
                  Mostrando {indiceInicio + 1} a {Math.min(indiceFim, notasFiltradas.length)} de {notasFiltradas.length} notas
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPaginaAtual(p => Math.max(1, p - 1))}
                    disabled={paginaAtual === 1}
                  >
                    ← Anterior
                  </Button>
                  <div className="flex items-center gap-2">
                    {Array.from({ length: Math.min(5, totalPaginas) }).map((_, i) => {
                      let pagina = i + 1;
                      if (totalPaginas > 5) {
                        if (paginaAtual > 3) {
                          pagina = paginaAtual - 2 + i;
                        }
                      }
                      if (pagina > totalPaginas) return null;
                      return (
                        <Button
                          key={pagina}
                          variant={paginaAtual === pagina ? "default" : "outline"}
                          size="sm"
                          onClick={() => setPaginaAtual(pagina)}
                        >
                          {pagina}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPaginaAtual(p => Math.min(totalPaginas, p + 1))}
                    disabled={paginaAtual === totalPaginas}
                  >
                    Próxima →
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog de Cancelamento */}
        <Dialog open={dialogCancelarOpen} onOpenChange={setDialogCancelarOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancelar Nota Fiscal</DialogTitle>
              <DialogDescription>
                Informe a justificativa para cancelamento da nota fiscal.
                A justificativa deve ter entre 15 e 255 caracteres.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="justificativa">Justificativa *</Label>
                <Input
                  id="justificativa"
                  value={justificativaCancelamento}
                  onChange={(e) => setJustificativaCancelamento(e.target.value)}
                  placeholder="Ex: Erro no preenchimento dos dados do cliente"
                  maxLength={255}
                />
                <p className="text-xs text-muted-foreground">
                  {justificativaCancelamento.length}/255 caracteres (mínimo: 15)
                </p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>Atenção:</strong> O cancelamento de nota fiscal só é permitido em até 24 horas após a emissão.
                  Após esse prazo, algumas SEFAZes podem não permitir o cancelamento.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setDialogCancelarOpen(false);
                setJustificativaCancelamento('');
                setNotaParaCancelar(null);
              }} disabled={cancelando}>
                Cancelar
              </Button>
              <Button
                onClick={confirmarCancelamento}
                disabled={cancelando || justificativaCancelamento.length < 15}
                className="bg-red-600 hover:bg-red-700"
              >
                {cancelando ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cancelando...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Confirmar Cancelamento
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de Carta de Correção */}
        <Dialog open={dialogCartaCorrecaoOpen} onOpenChange={setDialogCartaCorrecaoOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Emitir Carta de Correção Eletrônica (CCe)</DialogTitle>
              <DialogDescription>
                Informe o texto da correção que será registrado na Carta de Correção Eletrônica.
                Você pode emitir até 20 correções diferentes para a mesma nota.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="correcao">Texto da Correção *</Label>
                <textarea
                  id="correcao"
                  value={textoCorrecao}
                  onChange={(e) => setTextoCorrecao(e.target.value)}
                  placeholder="Ex: Correção do endereço do destinatário: Rua A, 123"
                  className="w-full min-h-[120px] p-3 border rounded-lg resize-none"
                  maxLength={1000}
                />
                <p className="text-xs text-muted-foreground">
                  {textoCorrecao.length}/1000 caracteres
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Informação:</strong> A Carta de Correção pode ser usada para corrigir:
                </p>
                <ul className="text-sm text-blue-700 list-disc list-inside mt-2 space-y-1">
                  <li>Dados cadastrais (endereço, telefone, etc.)</li>
                  <li>Observações e informações complementares</li>
                </ul>
                <p className="text-sm text-blue-800 mt-2">
                  <strong>Não pode corrigir:</strong> valores, quantidades, base de cálculo, alíquota, 
                  dados do remetente/destinatário que mudem a identidade, ou data de emissão.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setDialogCartaCorrecaoOpen(false);
                setTextoCorrecao('');
                setNotaParaCorrecao(null);
              }} disabled={emitindoCartaCorrecao}>
                Cancelar
              </Button>
              <Button
                onClick={confirmarCartaCorrecao}
                disabled={emitindoCartaCorrecao || !textoCorrecao.trim()}
              >
                {emitindoCartaCorrecao ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Emitindo...
                  </>
                ) : (
                  <>
                    <FileEdit className="mr-2 h-4 w-4" />
                    Emitir Carta de Correção
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={dialogReemitirOpen} onOpenChange={setDialogReemitirOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reemitir Nota Fiscal</DialogTitle>
              <DialogDescription>
                Esta ação cria uma nova referência e uma nova numeração. Use apenas após corrigir os dados necessários.
              </DialogDescription>
            </DialogHeader>
            {!notaParaReemitir?.dados_enviados && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Esta nota não possui payload salvo. Vamos reconstruir os dados a partir do pedido antes de reemitir.
              </div>
            )}
            {(notaParaReemitir?.dados_retornados as any)?.requires_numero_update && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                A SEFAZ indicou duplicidade de numeração. Ajuste o próximo número antes de reemitir.
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setDialogReemitirOpen(false);
                  setNotaParaReemitir(null);
                }}
                disabled={reemitindo}
              >
                Cancelar
              </Button>
              <Button onClick={handleConfirmarReemitir} disabled={reemitindo}>
                {reemitindo ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Reemitindo...
                  </>
                ) : (
                  <>
                    <FileEdit className="mr-2 h-4 w-4" />
                    Confirmar Reemissão
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Emitir NF Avulsa */}
        <DialogEmitirNFAvulsa
          open={dialogEmitirAvulsaOpen}
          onOpenChange={setDialogEmitirAvulsaOpen}
          onNFEmitida={loadNotas}
        />
      </div>
    </div>
  );
}
